import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  Box, 
  Button, 
  Container, 
  Grid, 
  Typography, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import customTheme from '../../../theme'; // Import the custom theme
import useMediaQuery from '@mui/material/useMediaQuery';
import { PrimaryButton } from '../../../SystemDesign/styles';
import { useTranslation } from 'react-i18next';
import { isProfessionalAcquisitionAllowed } from '../../../utils/nativePlatform';

// Icons for professional benefits
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import GroupsIcon from '@mui/icons-material/Groups';
import SpaIcon from '@mui/icons-material/Spa';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import AuthModal from '../../Auth/AuthModal';
import teamManagementImage from '../../../assets/landing-pro-team-management.png';
import servicesShowcaseImage from '../../../assets/landing-pro-services-bw.png';
import growthDashboardImage from '../../../assets/landing-pro-growth-dashboard.png';

// Benefit card component for professionals
const BenefitCard = ({ icon, title, description, image }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        backgroundColor: '#ffffff',
        borderRadius: customTheme.borderRadius.xl,
        overflow: 'hidden',
        boxShadow: customTheme.boxShadow.md,
        height: '100%',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-5px)',
          boxShadow: customTheme.boxShadow.lg,
        },
        border: `1px solid ${customTheme.colors.secondary[100]}`,
      }}
    >
      {/* Image section */}
      <Box
        sx={{
          width: { xs: '100%', md: '40%' },
          height: { xs: '200px', md: '100%' },
          minHeight: { xs: '200px', md: '280px' },
          backgroundImage: `url(${image})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          position: 'relative',
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.1)',
          }
        }}
      />
      
      {/* Content section */}
      <Box
        sx={{
          width: { xs: '100%', md: '60%' },
          padding: { xs: '24px', sm: '28px', md: '32px' },
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: '16px',
          }}
        >
          <Box
            sx={{
              backgroundColor: customTheme.colors.secondary[100],
              borderRadius: '50%',
              width: { xs: 48, md: 56 },
              height: { xs: 48, md: 56 },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: '16px',
            }}
          >
            {React.cloneElement(icon, { 
              sx: { 
                fontSize: { xs: 24, md: 28 }, 
                color: customTheme.colors.secondary[600] 
              } 
            })}
          </Box>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.375rem' },
              color: customTheme.colors.secondary[700],
              lineHeight: 1.3,
            }}
          >
            {title}
          </Typography>
        </Box>
        
        <Typography
          variant="body1"
          sx={{
            color: customTheme.colors.text.secondary,
            fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' },
            lineHeight: 1.6,
            mb: 3,
            textAlign: 'justify',
          }}
        >
          {description}
        </Typography>
      </Box>
    </Box>
  );
};

const ProSection = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [openDialog, setOpenDialog] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [userType, setUserType] = useState('client');
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const { t } = useTranslation();
  const brandName = t('brand.name');
  const professionalAcquisitionAllowed = isProfessionalAcquisitionAllowed();

  // Handle URL parameters for auth modal
  useEffect(() => {
    const type = searchParams.get('type');
    if (type === 'client' || type === 'pro') {
      setUserType(type);
      setAuthModalOpen(true);
      // Clean up URL
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('type');
      setSearchParams(newSearchParams);
    }
  }, [searchParams, setSearchParams]);

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleUserTypeSelect = (selectedUserType) => {
    setUserType(selectedUserType);
    setOpenDialog(false);
    setAuthModalOpen(true);
  };

  const handleScrollToSection = (sectionId) => {
    const el = document.querySelector(`#${sectionId}`)
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ behavior: "smooth", block: "start" })
    }
  }

  // Benefits data
  const benefits = [
    {
      icon: <CalendarMonthIcon />,
      title: t('landing_pro.benefits.appointments.title'),
      description: t('landing_pro.benefits.appointments.description'),
      image: "https://images.unsplash.com/photo-1556740738-b6a63e27c4df?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80"
    },
    {
      icon: <GroupsIcon />,
      title: t('landing_pro.benefits.team.title'),
      description: t('landing_pro.benefits.team.description'),
      image: teamManagementImage
    },
    {
      icon: <SpaIcon />,
      title: t('landing_pro.benefits.services.title'),
      description: t('landing_pro.benefits.services.description'),
      image: servicesShowcaseImage
    },
    {
      icon: <TrendingUpIcon />,
      title: t('landing_pro.benefits.growth.title'),
      description: t('landing_pro.benefits.growth.description'),
      image: growthDashboardImage
    }
  ];

  return (
    <Box
      sx={{
        py: { xs: 6, md: 10 },
        width: '100%',
      }}
      id="proSection"
    >
      <Container maxWidth="lg">
        {/* Section Header */}
        <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 7 } }}>
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem' },
              fontWeight: 700,
              color: customTheme.colors.secondary[700],
              marginBottom: { xs: 2, md: 3 },
              letterSpacing: '-0.01em',
            }}
          >
            {t('landing_pro.title')}
          </Typography>
          <Typography
            variant="h5"
            sx={{
              fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
              color: customTheme.colors.text.secondary,
              maxWidth: '800px',
              margin: '0 auto',
              lineHeight: 1.6,
              whiteSpace: 'pre-line',
            }}
          >
            {t('landing_pro.subtitle', { appName: brandName })}
          </Typography>
        </Box>

        {/* Benefits Grid */}
        <Grid container spacing={4} sx={{ mb: { xs: 5, md: 7 } }}>
          {benefits.map((benefit, index) => (
            <Grid item xs={12} key={index} sx={{ mb: { xs: 2, md: 3 } }}>
              <BenefitCard
                icon={benefit.icon}
                title={benefit.title}
                description={benefit.description}
                image={benefit.image}
              />
            </Grid>
          ))}
        </Grid>

        {/* Call to Action */}
        {professionalAcquisitionAllowed && (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            onClick={handleOpenDialog}
            variant="contained"
            sx={{
              ...PrimaryButton,
              backgroundColor: customTheme.colors.secondary[300],
              color: '#ffffff',
              fontSize: { xs: '0.9375rem', md: '1rem' },
              fontWeight: 700,
              padding: { xs: '14px 32px', md: '16px 36px' },
              borderRadius: '28px',
              textTransform: 'none',
              boxShadow: customTheme.boxShadow.md,
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor: customTheme.colors.secondary[400],
                transform: 'translateY(-3px)',
                boxShadow: '0 8px 25px rgba(226, 194, 156, 0.4)'
              }
            }}
          >
            {t('landing_pro.cta_join', { appName: brandName })}
          </Button>
          </Box>
        )}

        {/* User Type Selection Dialog */}
        {professionalAcquisitionAllowed && (
          <Dialog
          open={openDialog} 
          onClose={handleCloseDialog}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              p: isMobile ? 2 : 3,
              textAlign: 'center',
            }
          }}
        >
          <DialogTitle 
            sx={{ 
              fontSize: isMobile ? '1.5rem' : '1.75rem',
              fontWeight: 700,
              color: customTheme.colors.secondary[700],
              pb: 1,
            }}
          >
            {t('landing_pro.dialog.title', { appName: brandName })}
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1" sx={{ mb: 3, color: customTheme.colors.text.secondary, textAlign: 'justify' }}>
              {t('landing_pro.dialog.subtitle')}
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 2, mt: 2 }}>
              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={() => handleUserTypeSelect('client')}
                startIcon={<PersonIcon />}
                sx={{
                  py: 2,
                  borderRadius: 2,
                  backgroundColor: customTheme.colors.secondary[600],
                  color: 'white',
                  '&:hover': {
                    backgroundColor: customTheme.colors.secondary[500],
                  },
                }}
              >
                <Box sx={{ textAlign: 'left' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'white' }}>{t('landing_pro.dialog.client_title')}</Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>{t('landing_pro.dialog.client_desc')}</Typography>
                </Box>
              </Button>
              
              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={() => handleUserTypeSelect('pro')}
                startIcon={<BusinessIcon />}
                sx={{
                  py: 2,
                  borderRadius: 2,
                  backgroundColor: customTheme.colors.primary[500],
                  '&:hover': {
                    backgroundColor: customTheme.colors.primary[600],
                  },
                }}
              >
                <Box sx={{ textAlign: 'left' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'white' }}>{t('landing_pro.dialog.pro_title')}</Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>{t('landing_pro.dialog.pro_desc')}</Typography>
                </Box>
              </Button>
            </Box>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', pb: 3, px: 3 }}>
            <Button 
              onClick={handleCloseDialog}
              sx={{ color: customTheme.colors.text.secondary }}
            >
              {t('actions.cancel')}
            </Button>
          </DialogActions>
          </Dialog>
        )}

        {/* Auth Modal */}
        {professionalAcquisitionAllowed && (
          <AuthModal
            open={authModalOpen}
            onClose={() => setAuthModalOpen(false)}
            userType={userType}
          />
        )}
      </Container>
    </Box>
  );
};

export default ProSection;
